import { matchPath } from "react-router";
import { get } from "lodash";
import { uiShaShort, uiBuild } from "src/util/diagnostics";
import { genericPathname } from "src/util/url";
import {
  PATH_IDENTITY_KEYS,
  REQUIRED_ENVELOPE_KEYS,
} from "src/consts/analyticsManifest";
import {
  createAuth,
  getFromUserOrAuth,
  hasAdminRole,
  hasPowerRole,
  hasDirectoryRole,
  isInternalSupportUser,
} from "./auth";
// analytics is a global var injected by segment script
// analytics is not initialised on for local

// if window.analytics is not present, use this as last resort
const nullAnalyticsProxy = () => {
  return {
    identify: () => {},
    track: () => {},
    page: () => {},
    group: () => {},
    alias: () => {},
  };
};
let storedTenantId = null;
let segmentGlobalContext = {};
let segmentRequestContext = {};

export const ENVELOPE_KEYS = new Set([
  ...REQUIRED_ENVELOPE_KEYS,
  ...PATH_IDENTITY_KEYS,
]);

// analytics is a global var injected by segment script
// analytics is not initialised on for local
// evaluate from window on use, to avoid accessing stale pre-init version and losing messages
const getAnalytics = () => {
  return window.analytics || nullAnalyticsProxy();
};

const sha = uiShaShort() ?? "";
const build = uiBuild() ?? "";

export const getSegmentUserRole = (user) => {
  if (!user) {
    return null;
  }
  if (hasAdminRole(user)) {
    return "ADMIN";
  }
  if (hasPowerRole(user)) {
    return "POWER";
  }
  if (hasDirectoryRole(user)) {
    return "DIRECTORY";
  }
  return null;
};

export const setSegmentGlobalContext = (context = {}) => {
  segmentGlobalContext = { ...context };
};

export const getSegmentGlobalContext = () => ({ ...segmentGlobalContext });

export const setSegmentRequestContext = (context = {}) => {
  segmentRequestContext = { ...context };
};

export const getSegmentRequestContext = () => ({ ...segmentRequestContext });

export const clearSegmentRequestContext = () => {
  segmentRequestContext = {};
};

const getOperatorIdForSegment = (user) => {
  if (!user) {
    return null;
  }
  return getFromUserOrAuth(user, "operatorId", null) || null;
};

export const buildSegmentGlobalContext = ({
  user,
  userPerson,
  workspaceId,
  pagePath,
}) => {
  const { tenantId } = createAuth(user);
  const personId = userPerson?.aggregateId ?? null;

  return {
    tenant_id: tenantId ?? null,
    workspace_id: workspaceId ?? null,
    person_id: personId,
    operator_id: getOperatorIdForSegment(user),
    has_person_record: personId != null,
    user_role: getSegmentUserRole(user),
    page_path: pagePath,
  };
};

const getGlobalTrackProperties = () => ({
  event_source: "client",
  ui_version: sha,
  ui_build: build,
  ...segmentGlobalContext,
});

export const trackPage = (pathname, extraTracking = {}) => {
  const analytics = getAnalytics();

  if (!analytics) {
    return;
  }

  const name = genericPathname(pathname);
  const properties = {
    ...getGlobalTrackProperties(),
    page_path: name,
    ...extraTracking,
  };
  const options = storedTenantId ? { groupId: storedTenantId } : undefined;
  if (name !== "") {
    analytics.page(name, properties, options);
  } else {
    analytics.page(properties, options);
  }
};

export const identify = (user, userPerson) => {
  const analytics = getAnalytics();
  if (analytics && user) {
    const {
      createdAt: signedUp,
      loginsCount,
      anonymousId,
      roles,
      tenantId,
    } = createAuth(user);

    // temp to drop indentity calls for aooc tenant.
    if (tenantId === "aooc") {
      return;
    }

    const internalSupportUser = isInternalSupportUser(user);
    const role = getSegmentUserRole(user);
    // Date comes in ISO8601 string
    // (i.e. 2020-02-08 09:30:26.123 — with hour, minute, second, and millisecond time part).
    // Intercom integration requires UNIX timestamp (seconds).
    const createdAt = Math.floor(new Date(signedUp).valueOf() / 1000);
    const numberOfAllocations = userPerson
      ? get(userPerson, "allocations", []).length
      : null;
    analytics.identify(anonymousId, {
      createdAt,
      created_at: signedUp,
      logins_count: loginsCount,
      roles,
      role,
      is_internal_support_user: internalSupportUser,
      is_user_person: userPerson != null,
      number_of_allocations: numberOfAllocations,
      is_full_time: userPerson?.fte != null ? userPerson.fte >= 0.95 : null,
      span: userPerson?.span != null ? userPerson.span : null,
      layer: userPerson?.layers != null ? userPerson.layers : null,
    });
    if (tenantId && tenantId !== "TBC") {
      analytics.group(tenantId);
      storedTenantId = tenantId;
    }
  }
};

// This method is for extracting page info generated from the params of react-router's current matched path.
export const extractPageInfoForSegment = ({ routes, pathname }) => {
  const matchedPath = routes
    .map((route) => {
      const match = matchPath(pathname, route);

      if (match) {
        return { route, match };
      }

      return false;
    })
    .find((routeWithMatch) => routeWithMatch.match);
  const pageInfo = {};

  if (matchedPath?.match) {
    Object.entries(matchedPath.match.params).forEach(([key, value]) => {
      if (!value) {
        return;
      }

      /**
       * A few route params are generic with id.
       * In this case we rename the key to subjectId and extract the "subject" from the matched path.
       * */
      const isSubjectId = key === "id";

      if (isSubjectId) {
        pageInfo.subject = matchedPath.route.path
          .replace("/:workspaceId?", "")
          .replace(":", "_")
          .split("_")[0]
          .split("/")
          .filter((subStr) => subStr)
          .join("_");

        pageInfo.subjectId = value;
      } else {
        const paramKey = key === "workspaceId" ? "workspace_id" : key;
        pageInfo[paramKey] = value;
      }
    });
  }

  return pageInfo;
};

// LEGACY analytics — keep until 2026-11-17 (3 months after dual-emit landed).
// Do not remove before then; PostHog baselines depend on it.
export const trackEvent = (event, properties = {}) => {
  const analytics = getAnalytics();
  if (!analytics) {
    return;
  }

  const options = storedTenantId ? { groupId: storedTenantId } : undefined;
  analytics.track(
    event,
    { ...getGlobalTrackProperties(), ...properties },
    options
  );
};

export const pickJourneyEnvelope = (properties = {}) =>
  Object.fromEntries(
    Object.entries(properties).filter(([key]) => ENVELOPE_KEYS.has(key))
  );

export const buildJourneyTrackProperties = (properties = {}) => {
  const journeyProperties = {
    ...getGlobalTrackProperties(),
    ...segmentRequestContext,
    ...properties,
  };
  delete journeyProperties.schema_version;
  return journeyProperties;
};

export const assertRequiredJourneyEnvelope = (properties = {}) => {
  if (process.env.NODE_ENV === "production") {
    return;
  }

  const missingKeys = REQUIRED_ENVELOPE_KEYS.filter(
    (key) => properties[key] == null
  );
  if (missingKeys.length > 0) {
    console.warn(
      `Journey analytics event missing required envelope keys: ${missingKeys.join(
        ", "
      )}`
    );
  }
};

export const trackJourneyEvent = (event, properties = {}) => {
  // PAUSED 2026-08-31: journey dual-emit blew Segment MTU / consumption.
  // Call sites and envelope builders stay so we can re-enable after a
  // PostHog direct-ingest cost review. Legacy trackEvent() is unchanged.
  //
  // const analytics = getAnalytics();
  // if (!analytics) {
  //   return;
  // }
  //
  // const trackProperties = buildJourneyTrackProperties(properties);
  // assertRequiredJourneyEnvelope(trackProperties);
  //
  // const options = storedTenantId ? { groupId: storedTenantId } : undefined;
  // analytics.track(event, trackProperties, options);
  return;
};

const ANALYTICS_HEADER_KEYS = {
  user_role: "x-user-role",
  person_id: "x-person-id",
  page_path: "x-page-path",
  entry_point: "x-entry-point",
};

export const getAnalyticsEnrichmentHeaders = (overrides = {}) => {
  const context = {
    ...segmentGlobalContext,
    ...segmentRequestContext,
    ...overrides,
  };

  return Object.fromEntries(
    Object.entries(ANALYTICS_HEADER_KEYS)
      .filter(([key]) => context[key] != null)
      .map(([key, header]) => [header, String(context[key])])
  );
};
